/**
 * Lightweight i18n system — no library, no build step.
 * - 5 languages: English (default), Hindi, Marathi, Gujarati, Tamil
 * - URL convention: /, /hi, /mr, /gu, /ta as prefix for the public site
 * - useT() hook returns a translation function
 * - Translations are flat key/value, easy to extend page-by-page
 */

import { useLocation } from "react-router-dom";

export const LANGS = [
  { code: "en", label: "English", native: "English", dir: "ltr" },
  { code: "hi", label: "Hindi", native: "हिन्दी", dir: "ltr" },
  { code: "mr", label: "Marathi", native: "मराठी", dir: "ltr" },
  { code: "gu", label: "Gujarati", native: "ગુજરાતી", dir: "ltr" },
  { code: "ta", label: "Tamil", native: "தமிழ்", dir: "ltr" },
];

export const LANG_CODES = LANGS.map((l) => l.code);
const PREFIXED = LANG_CODES.filter((c) => c !== "en");

/**
 * Detect current language from the URL.
 * Examples:
 *   /            -> en
 *   /hi          -> hi
 *   /hi/pricing  -> hi
 *   /pricing     -> en
 */
export function getLangFromPath(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  return PREFIXED.includes(seg) ? seg : "en";
}

/**
 * Strip the language prefix from a path.
 * /hi/pricing -> /pricing ; /pricing -> /pricing ; /hi -> /
 */
export function stripLangPrefix(pathname) {
  const seg = pathname.split("/").filter(Boolean)[0];
  if (PREFIXED.includes(seg)) {
    const rest = pathname.replace(`/${seg}`, "");
    return rest === "" ? "/" : rest;
  }
  return pathname;
}

/**
 * Build a language-prefixed URL.
 *   prefixPath('/pricing', 'hi') -> '/hi/pricing'
 *   prefixPath('/',         'en') -> '/'
 */
export function prefixPath(path, lang) {
  if (lang === "en" || !LANG_CODES.includes(lang)) return path;
  if (path === "/") return `/${lang}`;
  return `/${lang}${path}`;
}

// ──────────────────────────────────────────────────────────────────
// Translation dictionaries — flat key/value per language.
// Add keys as you internationalise more pages.
// ──────────────────────────────────────────────────────────────────

const T = {
  en: {
    // Nav
    "nav.home": "Home",
    "nav.findCA": "Find CA",
    "nav.forCA": "For CAs",
    "nav.blog": "Blog",
    "nav.pricing": "Pricing",
    "nav.signIn": "Sign in",
    "nav.signUp": "Get Started Free",

    // Homepage
    "home.hero.eyebrow": "FINANCE-GRADE INVOICING",
    "home.hero.h1.line1": "Easy Billing, Quotations &",
    "home.hero.h1.line2": "GST Invoices",
    "home.hero.h1.line3": "for Growing Businesses",
    "home.hero.subhead": "Create invoices, quotations, track payments, manage GST billing and grow your business with InvoiceSetu — fast, simple and made for India.",
    "home.hero.ctaBusiness": "I'm a Business Owner",
    "home.hero.ctaCA": "I'm a CA / Professional",
    "home.hero.trustGst": "GST Compliant",
    "home.hero.trustSecurity": "Bank-grade encryption",
    "home.hero.trustWorkflow": "CA + Client workflows",

    // FAQ section title
    "home.faq.title": "Frequently Asked Questions",
    "home.faq.subtitle": "Honest answers about pricing, GST compliance, and migrating to InvoiceSetu.",
    "home.popular": "Popular tools & templates",

    // Footer
    "footer.sign_in": "Sign in",
    "footer.pricing": "Pricing",
    "footer.blog": "Blog",
    "footer.forCA": "For CAs",
    "footer.sample": "Sample",
    "footer.tagline": "India's smart invoice software for small businesses",

    // Pricing page
    "pricing.headline": "Start free. Upgrade only when you need more control.",
    "pricing.subhead": "Powerful invoicing for every Indian business. No hidden fees, no surprises.",
    "pricing.introBadge": "Introductory pricing",
    "pricing.cta.signup": "Get Started Free",
    "pricing.cta.upgrade": "Upgrade Now",

    // CA landing
    "ca.hero.eyebrow": "For Chartered Accountants",
    "ca.hero.h1.line1": "One Workspace.",
    "ca.hero.h1.line2": "Every Client Book.",
    "ca.hero.subhead": "Stop juggling Tally, WhatsApp PDFs, missed-document follow-ups and last-week's GSTR-1 dump. InvoiceSetu gives Indian CAs one workspace to manage every client — invoices, filings, reconciliations, deadlines.",
    "ca.cta.primary": "Open Free CA Workspace",
    "ca.cta.discount": "Activate 50% CA Discount",

    // Language switcher
    "lang.switcher.label": "Language",
  },

  hi: {
    "nav.home": "होम",
    "nav.findCA": "CA खोजें",
    "nav.forCA": "CA के लिए",
    "nav.blog": "ब्लॉग",
    "nav.pricing": "मूल्य",
    "nav.signIn": "साइन इन",
    "nav.signUp": "मुफ्त शुरू करें",

    "home.hero.eyebrow": "वित्तीय-स्तर का इनवॉइसिंग",
    "home.hero.h1.line1": "आसान बिलिंग, कोटेशन और",
    "home.hero.h1.line2": "GST इनवॉइस",
    "home.hero.h1.line3": "बढ़ते व्यवसायों के लिए",
    "home.hero.subhead": "InvoiceSetu के साथ इनवॉइस बनाएं, कोटेशन भेजें, भुगतान ट्रैक करें, GST बिलिंग संभालें और अपना व्यवसाय बढ़ाएं — तेज़, सरल और भारत के लिए बना।",
    "home.hero.ctaBusiness": "मैं व्यवसाय का मालिक हूँ",
    "home.hero.ctaCA": "मैं CA / प्रोफेशनल हूँ",
    "home.hero.trustGst": "GST अनुरूप",
    "home.hero.trustSecurity": "बैंक-स्तर की एन्क्रिप्शन",
    "home.hero.trustWorkflow": "CA + ग्राहक वर्कफ़्लो",

    "home.faq.title": "अक्सर पूछे जाने वाले प्रश्न",
    "home.faq.subtitle": "मूल्य, GST अनुपालन और InvoiceSetu पर आने के बारे में ईमानदार उत्तर।",
    "home.popular": "लोकप्रिय टूल और टेम्पलेट",

    "footer.sign_in": "साइन इन",
    "footer.pricing": "मूल्य",
    "footer.blog": "ब्लॉग",
    "footer.forCA": "CA के लिए",
    "footer.sample": "नमूना",
    "footer.tagline": "छोटे व्यवसायों के लिए भारत का स्मार्ट इनवॉइस सॉफ्टवेयर",

    "pricing.headline": "मुफ्त शुरू करें। ज़रूरत हो तभी अपग्रेड करें।",
    "pricing.subhead": "हर भारतीय व्यवसाय के लिए शक्तिशाली इनवॉइसिंग। कोई छिपी हुई फीस नहीं।",
    "pricing.introBadge": "परिचयात्मक मूल्य",
    "pricing.cta.signup": "मुफ्त शुरू करें",
    "pricing.cta.upgrade": "अभी अपग्रेड करें",

    "ca.hero.eyebrow": "चार्टर्ड अकाउंटेंट के लिए",
    "ca.hero.h1.line1": "एक वर्कस्पेस।",
    "ca.hero.h1.line2": "हर क्लाइंट की बही।",
    "ca.hero.subhead": "टैली, व्हाट्सएप PDFs, छूटे हुए दस्तावेज़ों और GSTR-1 की अंतिम-समय की भागदौड़ से छुटकारा पाएं। InvoiceSetu भारतीय CAs को हर क्लाइंट के इनवॉइस, फाइलिंग, रिकंसिलिएशन और डेडलाइन एक ही जगह दिखाता है।",
    "ca.cta.primary": "मुफ्त CA वर्कस्पेस खोलें",
    "ca.cta.discount": "50% CA छूट सक्रिय करें",

    "lang.switcher.label": "भाषा",
  },

  mr: {
    "nav.home": "मुख्यपृष्ठ",
    "nav.findCA": "CA शोधा",
    "nav.forCA": "CA साठी",
    "nav.blog": "ब्लॉग",
    "nav.pricing": "किंमत",
    "nav.signIn": "साइन इन",
    "nav.signUp": "मोफत सुरू करा",

    "home.hero.eyebrow": "वित्तीय-दर्जाचे इनव्हॉइसिंग",
    "home.hero.h1.line1": "सोपी बिलिंग, कोटेशन आणि",
    "home.hero.h1.line2": "GST इनव्हॉइस",
    "home.hero.h1.line3": "वाढत्या व्यवसायांसाठी",
    "home.hero.subhead": "InvoiceSetu सह इनव्हॉइस तयार करा, कोटेशन पाठवा, पेमेंट ट्रॅक करा, GST बिलिंग व्यवस्थापित करा आणि आपला व्यवसाय वाढवा — जलद, सोपे आणि भारतासाठी बनलेले.",
    "home.hero.ctaBusiness": "मी व्यवसाय मालक आहे",
    "home.hero.ctaCA": "मी CA / व्यावसायिक आहे",
    "home.hero.trustGst": "GST अनुपालक",
    "home.hero.trustSecurity": "बँक-दर्जाची एन्क्रिप्शन",
    "home.hero.trustWorkflow": "CA + ग्राहक वर्कफ्लो",

    "home.faq.title": "वारंवार विचारले जाणारे प्रश्न",
    "home.faq.subtitle": "किंमत, GST अनुपालन आणि InvoiceSetu वर येण्याबद्दल प्रामाणिक उत्तरे.",
    "home.popular": "लोकप्रिय साधने आणि टेम्पलेट्स",

    "footer.sign_in": "साइन इन",
    "footer.pricing": "किंमत",
    "footer.blog": "ब्लॉग",
    "footer.forCA": "CA साठी",
    "footer.sample": "नमुना",
    "footer.tagline": "लहान व्यवसायांसाठी भारताचे स्मार्ट इनव्हॉइस सॉफ्टवेअर",

    "pricing.headline": "मोफत सुरू करा. गरज भासेल तेव्हाच अपग्रेड करा.",
    "pricing.subhead": "प्रत्येक भारतीय व्यवसायासाठी शक्तिशाली इनव्हॉइसिंग. कोणतेही लपलेले शुल्क नाही.",
    "pricing.introBadge": "परिचयात्मक किंमत",
    "pricing.cta.signup": "मोफत सुरू करा",
    "pricing.cta.upgrade": "आत्ता अपग्रेड करा",

    "ca.hero.eyebrow": "चार्टर्ड अकाउंटंटसाठी",
    "ca.hero.h1.line1": "एकच वर्कस्पेस.",
    "ca.hero.h1.line2": "प्रत्येक क्लायंटची वही.",
    "ca.hero.subhead": "Tally, WhatsApp PDFs, चुकलेली कागदपत्रे आणि GSTR-1 ची शेवटच्या-क्षणाची धावपळ संपवा. InvoiceSetu भारतीय CAs ला एका वर्कस्पेसमधून प्रत्येक क्लायंट सांभाळू देते — इनव्हॉइस, फाइलिंग, रिकंसिलिएशन, डेडलाइन.",
    "ca.cta.primary": "मोफत CA वर्कस्पेस उघडा",
    "ca.cta.discount": "50% CA सूट सक्रिय करा",

    "lang.switcher.label": "भाषा",
  },

  gu: {
    "nav.home": "હોમ",
    "nav.findCA": "CA શોધો",
    "nav.forCA": "CA માટે",
    "nav.blog": "બ્લોગ",
    "nav.pricing": "કિંમત",
    "nav.signIn": "સાઇન ઇન",
    "nav.signUp": "મફત શરૂ કરો",

    "home.hero.eyebrow": "નાણાકીય-ધોરણ ઇનવોઇસિંગ",
    "home.hero.h1.line1": "સરળ બિલિંગ, કોટેશન અને",
    "home.hero.h1.line2": "GST ઇનવોઇસ",
    "home.hero.h1.line3": "વધતા વ્યવસાયો માટે",
    "home.hero.subhead": "InvoiceSetu સાથે ઇનવોઇસ બનાવો, કોટેશન મોકલો, ચૂકવણી ટ્રૅક કરો, GST બિલિંગનું સંચાલન કરો અને તમારો વ્યવસાય વધારો — ઝડપી, સરળ અને ભારત માટે બનાવેલ.",
    "home.hero.ctaBusiness": "હું વ્યવસાય માલિક છું",
    "home.hero.ctaCA": "હું CA / વ્યવસાયિક છું",
    "home.hero.trustGst": "GST સુસંગત",
    "home.hero.trustSecurity": "બેંક-ધોરણ એન્ક્રિપ્શન",
    "home.hero.trustWorkflow": "CA + ગ્રાહક વર્કફ્લો",

    "home.faq.title": "વારંવાર પૂછાતા પ્રશ્નો",
    "home.faq.subtitle": "કિંમત, GST પાલન અને InvoiceSetu પર આવવા વિશે પ્રામાણિક જવાબો.",
    "home.popular": "લોકપ્રિય સાધનો અને ટેમ્પલેટ્સ",

    "footer.sign_in": "સાઇન ઇન",
    "footer.pricing": "કિંમત",
    "footer.blog": "બ્લોગ",
    "footer.forCA": "CA માટે",
    "footer.sample": "નમૂનો",
    "footer.tagline": "નાના વ્યવસાયો માટે ભારતનું સ્માર્ટ ઇનવોઇસ સોફ્ટવેર",

    "pricing.headline": "મફત શરૂ કરો. જરૂર પડે ત્યારે જ અપગ્રેડ કરો.",
    "pricing.subhead": "દરેક ભારતીય વ્યવસાય માટે શક્તિશાળી ઇનવોઇસિંગ. કોઈ છુપી ફી નહીં.",
    "pricing.introBadge": "પરિચય કિંમત",
    "pricing.cta.signup": "મફત શરૂ કરો",
    "pricing.cta.upgrade": "હવે અપગ્રેડ કરો",

    "ca.hero.eyebrow": "ચાર્ટર્ડ એકાઉન્ટન્ટ માટે",
    "ca.hero.h1.line1": "એક વર્કસ્પેસ.",
    "ca.hero.h1.line2": "દરેક ક્લાયન્ટની ચોપડી.",
    "ca.hero.subhead": "Tally, WhatsApp PDFs, ગુમ થયેલા દસ્તાવેજો અને GSTR-1 ની છેલ્લી-ઘડીની દોડધામથી મુક્તિ મેળવો. InvoiceSetu ભારતીય CAs ને એક જ વર્કસ્પેસ આપે છે — દરેક ક્લાયન્ટ, દરેક ઇનવોઇસ, ફાઇલિંગ, રિકન્સિલિએશન, ડેડલાઇન.",
    "ca.cta.primary": "મફત CA વર્કસ્પેસ ખોલો",
    "ca.cta.discount": "50% CA ડિસ્કાઉન્ટ સક્રિય કરો",

    "lang.switcher.label": "ભાષા",
  },

  ta: {
    "nav.home": "முகப்பு",
    "nav.findCA": "CA தேடுங்கள்",
    "nav.forCA": "CA க்கு",
    "nav.blog": "வலைப்பதிவு",
    "nav.pricing": "விலை",
    "nav.signIn": "உள்நுழை",
    "nav.signUp": "இலவசமாக தொடங்குங்கள்",

    "home.hero.eyebrow": "நிதி-தர விலைப்பட்டியல்",
    "home.hero.h1.line1": "எளிய பில்லிங், மேற்கோள்கள் மற்றும்",
    "home.hero.h1.line2": "GST விலைப்பட்டியல்",
    "home.hero.h1.line3": "வளரும் வணிகங்களுக்கு",
    "home.hero.subhead": "InvoiceSetu மூலம் விலைப்பட்டியல்களை உருவாக்குங்கள், மேற்கோள்களை அனுப்புங்கள், கட்டணங்களைக் கண்காணியுங்கள், GST பில்லிங்கை நிர்வகியுங்கள் மற்றும் உங்கள் வணிகத்தை வளர்த்து கொள்ளுங்கள் — வேகமான, எளிய மற்றும் இந்தியாவிற்காக வடிவமைக்கப்பட்டது.",
    "home.hero.ctaBusiness": "நான் வணிக உரிமையாளர்",
    "home.hero.ctaCA": "நான் CA / நிபுணர்",
    "home.hero.trustGst": "GST இணக்கம்",
    "home.hero.trustSecurity": "வங்கி-தர குறியாக்கம்",
    "home.hero.trustWorkflow": "CA + வாடிக்கையாளர் பணிப்பாய்வு",

    "home.faq.title": "அடிக்கடி கேட்கப்படும் கேள்விகள்",
    "home.faq.subtitle": "விலை, GST இணக்கம் மற்றும் InvoiceSetu க்கு மாறுவது பற்றிய நேர்மையான பதில்கள்.",
    "home.popular": "பிரபலமான கருவிகள் மற்றும் வார்ப்புருக்கள்",

    "footer.sign_in": "உள்நுழை",
    "footer.pricing": "விலை",
    "footer.blog": "வலைப்பதிவு",
    "footer.forCA": "CA க்கு",
    "footer.sample": "மாதிரி",
    "footer.tagline": "சிறு வணிகங்களுக்கான இந்தியாவின் ஸ்மார்ட் விலைப்பட்டியல் மென்பொருள்",

    "pricing.headline": "இலவசமாக தொடங்குங்கள். தேவைப்படும்போது மட்டுமே மேம்படுத்துங்கள்.",
    "pricing.subhead": "ஒவ்வொரு இந்திய வணிகத்திற்கும் சக்திவாய்ந்த விலைப்பட்டியல். மறைக்கப்பட்ட கட்டணங்கள் இல்லை.",
    "pricing.introBadge": "அறிமுக விலை",
    "pricing.cta.signup": "இலவசமாக தொடங்கு",
    "pricing.cta.upgrade": "இப்போது மேம்படுத்து",

    "ca.hero.eyebrow": "சார்ட்டர்டு அக்கவுண்டண்ட்களுக்கு",
    "ca.hero.h1.line1": "ஒரு பணியிடம்.",
    "ca.hero.h1.line2": "ஒவ்வொரு வாடிக்கையாளர் புத்தகம்.",
    "ca.hero.subhead": "Tally, WhatsApp PDFs, தவறவிட்ட ஆவணங்கள் மற்றும் GSTR-1 கடைசி நிமிட பரபரப்பை விட்டுவிடுங்கள். InvoiceSetu இந்திய CAs க்கு ஒரே பணியிடத்தில் ஒவ்வொரு வாடிக்கையாளரின் விலைப்பட்டியல், தாக்கல், சரிசெய்தல் மற்றும் காலக்கெடுக்களை வழங்குகிறது.",
    "ca.cta.primary": "இலவச CA பணியிடம் திற",
    "ca.cta.discount": "50% CA தள்ளுபடியை செயல்படுத்து",

    "lang.switcher.label": "மொழி",
  },
};

/** Hook: returns t(key) bound to the URL's current language. Falls back to English. */
export function useT() {
  const { pathname } = useLocation();
  const lang = getLangFromPath(pathname);
  const dict = T[lang] || T.en;
  const fallback = T.en;
  return {
    t: (key) => dict[key] ?? fallback[key] ?? key,
    lang,
  };
}
